import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TicketButtonCommunicationDto, TicketButtonDto, TicketStepDto, CommunicationPlaceholders, CommunicationTypeEnum, FileUploadDto } from '../../service/dto';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { Subscription } from 'rxjs';
import { HttpFileService } from '../../service/http-file.service';
import { NzModalRef } from 'ng-zorro-antd/modal';


export interface WrapperCommunication {
	payload: NotificationDto | TicketButtonCommunicationDto;
	form: FormGroup;
}

export interface NotificationDto {
	emailFrom: string;
	emailTo?: string;
	emailCc?: string;
	emailBcc?: string;
	emailSubject?: string;
	emailBody?: string;
	attachments?: FileUploadDto[];
	smsMsisdn?: string;
	smsMessage?: string;
	type: CommunicationTypeEnum;
}
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MSISDN_REGEX = /^[0-9]{10,12}$/;
const PLACEHOLDER_REGEX = /^\{\{[^{}]+\}\}$/;
export function emailOrPlaceholderValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const values: string[] = control.value
			.split(/[,;]+/)
			.map((v: string) => v.trim())
			.filter((v: string) => v.length > 0);

		const invalids = values.filter(v => !EMAIL_REGEX.test(v) && !PLACEHOLDER_REGEX.test(v));

		return invalids.length > 0 ? { emailOrPlaceholder: { invalid: invalids } } : null;
	};
}
export function atLeastOneRecipientValidator(): ValidatorFn {
	return (group: AbstractControl): ValidationErrors | null => {
		if (!(group instanceof FormGroup)) {
			return null;
		}
		if (group.get('type')!.value != 'Email') {
			return null;
		}

		const to = group.get('emailTo')?.value?.trim();
		const cc = group.get('emailCc')?.value?.trim();
		const bcc = group.get('emailBcc')?.value?.trim();

		if (to || cc || bcc) {
			return null;
		}

		return { atLeastOneRecipient: true };
	};
}
export function msisdnOrPlaceholderValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const values: string[] = control.value
			.split(/[,;]+/)
			.map((v: string) => v.trim())
			.filter((v: string) => v.length > 0);

		const invalids = values.filter(v => !MSISDN_REGEX.test(v) && !PLACEHOLDER_REGEX.test(v));

		return invalids.length > 0 ? { msisdnOrPlaceholder: { invalid: invalids } } : null;
	};
}
@Component({
	selector: 'app-compose-notification',
	standalone: false,
	templateUrl: './compose-notification.component.html',
	styleUrl: './compose-notification.component.scss'
})
export class ComposeNotificationComponent {
	@Input() showActions = true;
	@Input() placeholders!: CommunicationPlaceholders;
	_input!: WrapperCommunication;

	private subscription = new Subscription();

	constructor(private fb: FormBuilder, private fileService: HttpFileService, private modal: NzModalRef) {
	}

	@Input()
	set input(input: WrapperCommunication) {
		this._input = input;

		this.subscription.unsubscribe();
		this.subscription = new Subscription();

		this.subscription.add(this._input.form.get('type')!.valueChanges.subscribe(value => {
			if (value === 'Email') {
				this._input.form.removeControl('smsTo');
				this._input.form.removeControl('smsBody');
				if (!this._input.form.contains('emailTo')) {
					this._input.form.addControl('emailTo', this.fb.control(this._input.payload.emailTo, [Validators.required, emailOrPlaceholderValidator()]));
					this._input.form.addControl('emailCc', this.fb.control(this._input.payload.emailCc, [emailOrPlaceholderValidator()]));
					this._input.form.addControl('emailBcc', this.fb.control(this._input.payload.emailBcc, [emailOrPlaceholderValidator()]));
					this._input.form.addControl('emailSubject', this.fb.control(this._input.payload.emailSubject, [Validators.required]));
					this._input.form.addControl('emailBody', this.fb.control(this._input.payload.emailBody, [Validators.required]));
				}
			} else if (value === 'Sms') {
				this._input.form.removeControl('emailTo');
				this._input.form.removeControl('emailCc');
				this._input.form.removeControl('emailBcc');
				this._input.form.removeControl('emailSubject');
				this._input.form.removeControl('emailBody');

				if (!this._input.form.contains('smsTo')) {
					this._input.form.addControl('smsMsisdn', this.fb.control(this._input.payload.smsMsisdn, [Validators.required, msisdnOrPlaceholderValidator()]));
					this._input.form.addControl('smsMessage', this.fb.control(this._input.payload.smsMessage, [Validators.required]));
				}
			}
			const attachmentsArray = this.fb.array(
				(this._input.payload.attachments ?? []).map(att =>
					this.fb.group({
						fileName: this.fb.control(att.fileName ?? ''),
						name: this.fb.control(att.name ?? ''),
						selected: this.fb.control(att.selected ?? true),
						uuid: this.fb.control(att.uuid ?? null),
					})
				)
			);
			this._input.form.addControl('attachments', attachmentsArray);
		}));

		this.subscription.add(this._input.form.valueChanges.subscribe(value => {
			Object.assign(this._input.payload, value);
		}));

	}
	get input() {
		return this._input;
	}

	customUpload = (item: NzUploadXHRArgs): Subscription => {
		const formData = new FormData();
		formData.append('file', item.file as any);

		// Create the subscription
		const sub = this.fileService.upload(item.name ?? ('attachment-' + Date.now()), formData).subscribe({
			next: (res) => {
				// success callback
				item.onSuccess!(res, item.file, event);
				this._input.payload.attachments!.push(res);
				(this._input.form.get('attachments')! as FormArray).push(
					this.fb.group({
						name: this.fb.control(res.name ?? ''),
						fileName: this.fb.control(res.fileName ?? ''),
						selected: this.fb.control(res.selected ?? true),
						uuid: this.fb.control(res.uuid ?? null),
					}));
			},
			error: (err) => {
				item.onError!(err, item.file);
			}
		});
		this.subscription.add(sub);

		// return it so nz-upload can cancel if needed
		return sub;
	};
	onCancel(): void {
		this.modal.destroy({ send: false });
	}

	onSend(): void {
		if (this._input.form.valid) {
			this.modal.destroy({
				send: true,
				notification: this.toNotification()
			});
		}
	}
	toNotification(): NotificationDto {
		console.log("To Notification.");
		const attachments: FileUploadDto[] = [];
		for (let i = 0; i < ((this._input.form.get('attachments') as FormArray)!.value).length; i++) {
			if ((this._input.form.get('attachments') as FormArray)!.value[i] == true) {
				attachments.push(this._input.payload.attachments![i]);
			}
		}

		const type = this._input.form.get('type')?.value;
		if (type === 'Sms') {
			return {
				type: 'Sms',
				emailFrom: '-',
				smsMsisdn: this._input.form.get('smsMsisdn')?.value,
				smsMessage: this._input.form.get('smsMessage')?.value,
				attachments: attachments
			};
		}
		return {
			type: 'Email',
			emailFrom: '-',
			emailTo: this._input.form.get('emailTo')?.value,
			emailCc: this._input.form.get('emailCc')?.value,
			emailBcc: this._input.form.get('emailBcc')?.value,
			emailSubject: this._input.form.get('emailSubject')?.value,
			emailBody: this._input.form.get('emailBody')?.value,
			attachments: attachments
		};
	}
	get allAttachments(): FormGroup[] {
		return (this.input.form.get('attachments') as FormArray).controls as FormGroup[];
	}

	public static toWrapper(fb: FormBuilder, input: TicketButtonCommunicationDto | NotificationDto, step: TicketStepDto, additionalFiles: { [key: string]: string }): WrapperCommunication {
		const form = fb.group({});
		form.addControl('type', fb.control(input.type, [Validators.required]));

		if (input.type == 'Sms') {
			form.addControl('smsMsisdn', fb.control(input.smsMsisdn, [Validators.required, msisdnOrPlaceholderValidator()]));
			form.addControl('smsMessage', fb.control(input.smsMessage, [Validators.required]));
		}
		if (input.type == 'Email') {
			form.addControl('emailTo', fb.control(input.emailTo, [Validators.required, emailOrPlaceholderValidator()]));
			form.addControl('emailCc', fb.control(input.emailCc, [emailOrPlaceholderValidator()]));
			form.addControl('emailBcc', fb.control(input.emailBcc, [emailOrPlaceholderValidator()]));
			form.addControl('emailSubject', fb.control(input.emailSubject, [Validators.required]));
			form.addControl('emailBody', fb.control(input.emailBody, [Validators.required]));
		}
		const attachmentsArray = fb.array<FormGroup>([]);
		if (input.attachments) {
			input.attachments.map(item => {
				console.log("Adding addition attachments - From TicketButtonCommunicationDto", item);
				attachmentsArray.push(
					fb.group({
						refId: fb.control(item.refId!),
						fileName: fb.control(item.fileName ?? ''),
						name: fb.control(item.name ?? ''),
						selected: fb.control(item.selected ?? true),
						uuid: fb.control(item.uuid ?? null),
					})
				)
			})
		}
		if (step.availableAttachments) {
			Object.keys(step.availableAttachments)
				.map(item => step.availableAttachments[+item])
				.filter(item => !input.attachments?.some(i => i.refId == item.refId))
				.forEach(item => {
					console.log("Adding addition attachments - From step.availableAttachments", item);
					attachmentsArray.push(
						fb.group({
							refId: fb.control(item.refId!),
							fileName: fb.control(item.fileName ?? ''),
							name: fb.control(item.name ?? ''),
							selected: fb.control(false),
							uuid: fb.control(item.uuid ?? null),
						})
					)
				})
		}
		if (step.availableAttachmentsAdditional) {
			Object.keys(step.availableAttachmentsAdditional)
				.map(item => step.availableAttachmentsAdditional[+item])
				.filter(item => !input.attachments?.some(i => i.refId == item.refId))
				.forEach(item => {
					console.log("Adding addition attachments - From step.availableAttachmentsAdditional", item);
					attachmentsArray.push(
						fb.group({
							refId: fb.control(item.refId!),
							fileName: fb.control(item.fileName ?? ''),
							name: fb.control(item.name ?? ''),
							selected: fb.control(false),
							uuid: fb.control(item.uuid ?? null),
						})
					)
				})
		}
		if (additionalFiles) {
			Object.keys(additionalFiles)
				.map(item => ({ label: item, value: additionalFiles[item] }))
				.filter(item => !input.attachments?.some(i => i.uuid == item.value))
				.forEach(item => {
					console.log("Adding addition attachments - From additionalFiles", item);
					attachmentsArray.push(
						fb.group({
							fileName: fb.control(item.label ?? ''),
							name: fb.control(item.label ?? ''),
							selected: fb.control(false),
							uuid: fb.control(item.value ?? null),
						})
					)
				})
		}

		form.addControl('attachments', attachmentsArray);
		form.markAllAsTouched();
		form.updateValueAndValidity();
		return { payload: input, form: form }
	}
}
